/** Совпадает с UserPlan в `services/dataClient.ts`. */
export type PlanTier = 'free' | 'pro' | 'premium';

/** Нормализация значения плана из профиля/API. */
export function normalizePlanTier(raw: unknown): PlanTier {
  if (raw === 'free' || raw === 'pro' || raw === 'premium') return raw;
  if (typeof raw === 'string') {
    const s = raw.trim().toLowerCase();
    if (s === 'free' || s === 'pro' || s === 'premium') return s;
  }
  return 'free';
}

const PREMIUM_UNLIMITED = 99999;

/** Создание команды и приглашения участников: платные тарифы (не Free). */
export function planAllowsTeamInvites(plan: PlanTier | undefined): boolean {
  const p = plan ?? 'free';
  return p === 'pro' || p === 'premium';
}

/** Максимум комнат на владельца */
export const MAX_ROOMS: Record<PlanTier, number> = {
  free: 3,
  pro: PREMIUM_UNLIMITED,
  premium: PREMIUM_UNLIMITED,
};

/** Максимум таймеров в одной комнате */
export const MAX_TIMERS_PER_ROOM: Record<PlanTier, number> = {
  free: 3,
  pro: 50,
  premium: 500,
};

/** Максимум форм сообщений (карточек) в комнате */
export const MAX_MESSAGE_FORMS_PER_ROOM: Record<PlanTier, number> = {
  free: 3,
  pro: PREMIUM_UNLIMITED,
  premium: PREMIUM_UNLIMITED,
};

/** Участников команды всего (включая владельца) */
export const MAX_TEAM_MEMBERS: Record<PlanTier, number> = {
  free: 1,
  pro: 5,
  premium: 20,
};

/** Дефолтное значение maxLiveConnections при создании комнаты */
export const DEFAULT_LIVE_CONNECTIONS: Record<PlanTier, number> = {
  free: 3,
  pro: 5,
  premium: 50,
};

/**
 * Нижняя граница настраиваемого лимита live connections (для pro/premium).
 * Free: совпадает с верхней (фиксировано).
 */
export const MIN_LIVE_CONNECTIONS: Record<PlanTier, number> = {
  free: 3,
  pro: 5,
  premium: 50,
};

/** Верхняя граница настраиваемого maxLiveConnections */
export const MAX_LIVE_CONNECTIONS_CAP: Record<PlanTier, number> = {
  free: 3,
  pro: 25,
  premium: 300,
};

/**
 * @deprecated Используйте DEFAULT_LIVE_CONNECTIONS и MAX_LIVE_CONNECTIONS_CAP.
 * Оставлено для постепенной замены: то же, что DEFAULT_LIVE_CONNECTIONS.
 */
export const MAX_LIVE_CONNECTIONS: Record<PlanTier, number> = {
  free: DEFAULT_LIVE_CONNECTIONS.free,
  pro: DEFAULT_LIVE_CONNECTIONS.pro,
  premium: DEFAULT_LIVE_CONNECTIONS.premium,
};

export function getMaxRoomsForPlan(plan: PlanTier | undefined): number {
  const p = plan ?? 'free';
  return MAX_ROOMS[p] ?? MAX_ROOMS.free;
}

/** Без числового лимита комнат в UI (сентинел `PREMIUM_UNLIMITED`). */
export function isUnlimitedRoomsForPlan(plan: PlanTier | undefined): boolean {
  return getMaxRoomsForPlan(plan) >= PREMIUM_UNLIMITED;
}

export function getMaxTimersPerRoomForPlan(plan: PlanTier | undefined): number {
  const p = plan ?? 'free';
  return MAX_TIMERS_PER_ROOM[p] ?? MAX_TIMERS_PER_ROOM.free;
}

export function getMaxMessageFormsPerRoomForPlan(plan: PlanTier | undefined): number {
  const p = plan ?? 'free';
  return MAX_MESSAGE_FORMS_PER_ROOM[p] ?? MAX_MESSAGE_FORMS_PER_ROOM.free;
}

/** Без числового лимита форм сообщений в UI (сентинел `PREMIUM_UNLIMITED`). */
export function isUnlimitedMessageFormsForPlan(plan: PlanTier | undefined): boolean {
  return getMaxMessageFormsPerRoomForPlan(plan) >= PREMIUM_UNLIMITED;
}

export function getMaxTeamMembersForPlan(plan: PlanTier | undefined): number {
  const p = plan ?? 'free';
  return MAX_TEAM_MEMBERS[p] ?? MAX_TEAM_MEMBERS.free;
}

export function getDefaultLiveConnectionsForPlan(plan: PlanTier | undefined): number {
  const p = plan ?? 'free';
  return DEFAULT_LIVE_CONNECTIONS[p] ?? DEFAULT_LIVE_CONNECTIONS.free;
}

/** Верхняя граница настройки (не путать с «текущим» значением в комнате) */
export function getMaxLiveConnectionsCapForPlan(plan: PlanTier | undefined): number {
  const p = plan ?? 'free';
  return MAX_LIVE_CONNECTIONS_CAP[p] ?? MAX_LIVE_CONNECTIONS_CAP.free;
}

/**
 * Текущий лимит подключений для отображения и проверки блокировки:
 * значение из комнаты, приведённое к допустимому диапазону лицензии владельца.
 */
export function resolveLiveConnectionsLimit(
  stored: number | undefined,
  plan: PlanTier | undefined,
): number {
  return clampLiveConnectionsToPlan(stored, plan);
}

/**
 * Приводит желаемое значение к [min..cap] для лицензии.
 * Если stored не задан — возвращает дефолт лицензии.
 */
export function clampLiveConnectionsToPlan(
  stored: number | undefined,
  plan: PlanTier | undefined,
): number {
  const p = plan ?? 'free';
  const def = getDefaultLiveConnectionsForPlan(p);
  const cap = getMaxLiveConnectionsCapForPlan(p);
  const min = MIN_LIVE_CONNECTIONS[p] ?? MIN_LIVE_CONNECTIONS.free;
  if (stored === undefined || !Number.isFinite(stored) || stored < 1) {
    return def;
  }
  return Math.min(cap, Math.max(min, Math.floor(stored)));
}

/** @deprecated Используйте getDefaultLiveConnectionsForPlan */
export function getMaxLiveConnectionsForPlan(plan: PlanTier | undefined): number {
  return getDefaultLiveConnectionsForPlan(plan);
}
